import React, { useEffect, useState, useMemo, useCallback } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  Grid,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Button as MuiButton,
  Divider,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import SelectWrapper from "./SelectBox";
import { useDropzone } from "react-dropzone";
import moment from "moment";
import apiUtils from "../utils/apiUtils";
import { useTranslation } from "react-i18next";
import ClaimPaymentFields from "./ClaimPaymentFields";
import { translateExciseClaimStatus } from "../utils/claimStatusUtils";

const Card = styled(MuiCard)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Button = styled(MuiButton)(spacing);

// const timeOut = (time) => new Promise((res) => setTimeout(res, time));

export default function ViewExciseClaimDialog(props) {
  const { t } = useTranslation();
  const { viewExciseClaimDialog, setViewExciseClaimDialog, refreshGrid } = props;

  const validationSchema = Yup.object().shape({
    claimCode: Yup.string().required(t("Required")),
    countryId: Yup.string().required(t("Required")),
    startMonth: Yup.string().required(t("Required")),
    endMonth: Yup.string().required(t("Required")),
    year: Yup.string().required(t("Required")),
    claimDate: Yup.string().required(t("Required")),
    // claimStateId: Yup.string().required(t("Required")),
    liters: Yup.string().required(t("Required")),
    claimAmountEUR: Yup.string()
      .required(t("Required"))
      .matches(/^-?[0-9]+(\.[0-9]+)?$/, t("Invalid format. Only numbers, dots, and an optional negative sign are allowed.")),
    claimAmountLocal: Yup.string()
      .required(t("Required"))
      .matches(/^-?[0-9]+(\.[0-9]+)?$/, t("Invalid format. Only numbers, dots, and an optional negative sign are allowed.")),
  });

  // console.log(viewExciseClaimDialog);

  const [selectedClaimDate, setSelectedClaimDate] = useState("");
  const [selectedRefundDate, setSelectedRefundDate] = useState("");
  const [isNewFile, setIsNewFile] = useState(false);
  const [isRemovedFile, setIsRemovedFile] = useState(false);
  // const [isUpdatedFile, setIsUpdatedFile] = useState(false);
  // const [fileContent, setFileContent] = useState(null);
  // const [editedData, setEditedData] = useState({});
  // const [currentCountryCode, setCurrentCountryCode] = useState(
  //   viewExciseClaimDialog?.params?.countryCode
  // );
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [customerPreFinanceData, setCustomerPreFinanceData] = useState([]);

  const countryData = apiUtils.useApiData("/api/v1/countries");

  const handleConfirmationDialogClose = () => {
    setShowConfirmationDialog(false);
  };

  // const handleRemoveFileClick = () => {
  //   setShowConfirmationDialog(true);
  // };

  var customerId = viewExciseClaimDialog?.custInfo[0]?.customerId;
  // var customerNumber = viewExciseClaimDialog?.custInfo[0]?.customerNumber;

  // useEffect(() => {
  //   setCurrentCountryCode(viewExciseClaimDialog?.params?.countryId);
  // }, [viewExciseClaimDialog?.params?.countryId]);

  useEffect(() => {
    if (customerId) {
      axios.get("/api/v1/preFinanceByEntityId/" + customerId).then((response) => setCustomerPreFinanceData(response.data));
    }
  }, [customerId]);

  useEffect(() => {
    const nonRealDates = [
      "0000-00-00",
      "01-01-1900",
      "01-01-1970",
      "00-00-0000",
      "01-01-0001",
      "0001-01-01",
      "01-01-1",
      "0001-01-01T00:00:00",
      "1-1-1",
      "01-1-1",
      "1-01-1",
    ];

    const isValidDate = (dateString) => {
      // Return false for null, undefined, or empty string
      if (!dateString || typeof dateString !== "string") return false;

      // Normalize the date string by removing time component if present
      const normalizedDate = dateString.split("T")[0];

      // Check against known invalid dates
      if (nonRealDates.includes(normalizedDate)) return false;

      // Try parsing with moment using strict mode
      const date = moment(dateString, moment.ISO_8601, true);

      // Check if the date is valid and after 1970
      return date.isValid() && date.year() > 1970;
    };

    if (viewExciseClaimDialog?.params?.preFinanceId > 0) {
      const found = customerPreFinanceData.find((item) => item.preFinanceId === viewExciseClaimDialog.params.preFinanceId);
      if (found && found.payedOut === 1) {
        setSelectedRefundDate(moment(found.payoutDate).format("YYYY-MM-DD"));
      } else {
        setSelectedRefundDate("N/A");
      }
      setSelectedClaimDate("N/A");
    } else {
      const claimCode = viewExciseClaimDialog?.params?.claimCode;
      const lastTwoDigits = claimCode?.slice(-2);

      // For claimCode ending in 11, always set N/A
      if (lastTwoDigits === "11") {
        setSelectedClaimDate("N/A");
        setSelectedRefundDate("N/A");
        return;
      }

      // Handle claim date
      const claimDate = viewExciseClaimDialog?.params?.claimDate;
      if (isValidDate(claimDate)) {
        setSelectedClaimDate(moment(claimDate).format("YYYY-MM-DD"));
      } else {
        setSelectedClaimDate("N/A");
      }

      // Handle refund date
      const refundDate = viewExciseClaimDialog?.params?.reimbursementDate;
      if (isValidDate(refundDate)) {
        setSelectedRefundDate(moment(refundDate).format("YYYY-MM-DD"));
      } else {
        setSelectedRefundDate("N/A");
      }
    }
  }, [viewExciseClaimDialog, customerPreFinanceData]);

  // useEffect(() => {
  //   if (viewExciseClaimDialog.isOpen) {
  //     setCurrentInvoiceState(viewExciseClaimDialog.params.invoiceStateId);
  //   }
  // }, [viewExciseClaimDialog]);

  const [fileUrl, setFileUrl] = useState(null);

  const downloadFile = async (fileId) => {
    setFileUrl("");
    try {
      const response = await axios({
        url: "/api/v1/azureblobstorage/" + fileId,
        method: "GET",
        responseType: "blob",
      });
      const href = URL.createObjectURL(response.data);
      setFileUrl(href);
    } catch (error) {
      setFileUrl("NoFile");
      console.error("Error downloading file:", error);
    }
  };

  if (viewExciseClaimDialog?.downloadFile) {
    const fileId = viewExciseClaimDialog?.params?.fileId;
    // console.log("Download file: " + fileId);
    if (fileId === 0 || fileId === null) {
      setFileUrl("NoFile");
    } else {
      downloadFile(fileId);
    }
    viewExciseClaimDialog.downloadFile = false;
  }

  const initialValues = {
    customerId: customerId,
    claimCode: viewExciseClaimDialog?.params?.claimCode,
    taxOfficeRefNo: viewExciseClaimDialog?.params?.taxOfficeRefNo,
    claimDate: viewExciseClaimDialog?.params?.claimDate,
    countryId: viewExciseClaimDialog?.params?.countryId,
    claimAmountEUR: viewExciseClaimDialog?.params?.claimAmountEUR,
    // countryId: viewExciseClaimDialog?.params?.countryId,
    claimAmountLocal: viewExciseClaimDialog?.params?.claimAmountLocal,
    startMonth: viewExciseClaimDialog?.params?.startMonth,
    endMonth: viewExciseClaimDialog?.params?.endMonth,
    year: viewExciseClaimDialog?.params?.year,
    liters: viewExciseClaimDialog?.params?.liters,
  };

  const baseStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
    minHeight: "300px",
    minWidth: "600px",
    marginTop: "150px",
    marginLeft: "200px",
  };

  const focusedStyle = {
    borderColor: "#2196f3",
  };

  const acceptStyle = {
    borderColor: "#00e676",
  };

  const rejectStyle = {
    borderColor: "#ff1744",
  };

  const onDropAccepted = useCallback(
    (acceptedFiles) => {
      const selectedFile = acceptedFiles[0];
      const href = URL.createObjectURL(selectedFile);
      setFileUrl(href);
      if (isRemovedFile) {
        if (!isNewFile) {
          // setIsUpdatedFile(true);
        }
      } else {
        setIsNewFile(true);
      }
      // setFileContent(selectedFile);
      setIsRemovedFile(false);
      refreshGrid();
    },
    [isRemovedFile, isNewFile, refreshGrid]
  );

  function StyledDropzone(props) {
    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
      accept: { "application/pdf": [] },
      onDropAccepted,
    });

    const style = useMemo(
      () => ({
        ...baseStyle,
        ...(isFocused ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {}),
      }),
      [isFocused, isDragAccept, isDragReject]
    );

    return (
      <div className="container">
        <div {...getRootProps({ style })}>
          <input {...getInputProps()} />

          <p
            style={{
              display: "flex",
              margin: "auto",
              justifyContent: "center",
            }}
          >
            {t("No files are currently attached to this invoice.")} <br />
            {t("Drag 'n' drop the invoice file here, or click to select")}
          </p>
        </div>
      </div>
    );
  }

  const displayClaimDate = selectedClaimDate === "N/A" ? t("N/A") : selectedClaimDate;
  const displayRefundDate = selectedRefundDate === "N/A" ? t("N/A") : selectedRefundDate;
  const claimCode = viewExciseClaimDialog?.params?.claimCode ?? "";
  const showReceivedPaymentDetails = String(claimCode).endsWith("0");

  const handlePaymentSaved = useCallback(async () => {
    const claimId = viewExciseClaimDialog?.params?.claimId;
    if (!claimId) {
      return;
    }

    if (typeof refreshGrid === "function") {
      await refreshGrid();
    }

    try {
      const response = await axios.get(`/api/v1/exciseClaims/${claimId}`);
      setViewExciseClaimDialog((prev) => ({
        ...prev,
        params: {
          ...prev.params,
          ...response.data,
          status: prev.params?.status,
        },
      }));
    } catch (error) {
      console.error("Failed to refresh excise claim payment details", error);
    }
  }, [viewExciseClaimDialog?.params?.claimId, refreshGrid, setViewExciseClaimDialog]);

  return (
    <Dialog
      open={viewExciseClaimDialog.isOpen}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle id="alert-dialog-title">{viewExciseClaimDialog.title}</DialogTitle>
      <Divider />
      <DialogContent>
        <Formik initialValues={initialValues} validationSchema={validationSchema}>
          {({ errors, handleBlur, handleChange, isSubmitting, touched, values, status }) => (
            <Card mb={6}>
              <CardContent>
                {isSubmitting ? (
                  <Box minHeight="700px" display="flex" justifyContent="center" my={20} mx={40}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <form>
                    <Grid container spacing={6}>
                      <Grid item md={3} xs={12}>
                        {/* <Grid item xs={12}>
                          <TextField
                            disabled
                            name="entityId"
                            label="Customer number"
                            value={customerNumber}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="name"
                            variant="outlined"
                            my={2}
                          />
                        </Grid> */}
                        <Grid item xs={12}>
                          <TextField
                            disabled
                            name="claimCode"
                            label={t("Claim code")}
                            value={values.claimCode}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="name"
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            disabled
                            name="taxOfficeRefNo"
                            label={t("Reference Number")}
                            value={values.taxOfficeRefNo == null ? t("N/A") : values.taxOfficeRefNo}
                            error={Boolean(touched.taxOfficeRefNo && errors.taxOfficeRefNo)}
                            fullWidth
                            helperText={touched.taxOfficeRefNo && errors.taxOfficeRefNo}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="taxOfficeRefNo"
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                        <Grid xs={12}>
                          <SelectWrapper
                            disabled
                            name="countryId"
                            label={t("Country")}
                            options={JSON.parse(
                              JSON.stringify(JSON.parse(JSON.stringify(countryData).split('"countryId":').join('"key":')))
                                .split('"countryCode":')
                                .join('"value":')
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            disabled
                            name="startMonth"
                            label={t("Start month")}
                            value={values.startMonth}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="name"
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            disabled
                            name="endMonth"
                            label={t("End month")}
                            value={values.endMonth}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="name"
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            disabled
                            name="year"
                            label={t("Year")}
                            value={values.year}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="name"
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            disabled
                            id="claimDate"
                            name="claimDate"
                            label={t("Claim date")}
                            type={selectedClaimDate === "N/A" ? "text" : "date"}
                            value={displayClaimDate}
                            fullWidth
                            InputLabelProps={{
                              shrink: true,
                            }}
                            my={1}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            disabled
                            id="refundDate"
                            name="refundDate"
                            label={t("Pay date")}
                            type={selectedRefundDate === "N/A" ? "text" : "date"}
                            value={displayRefundDate}
                            fullWidth
                            InputLabelProps={{
                              shrink: true,
                            }}
                            my={2}
                          />
                        </Grid>
                        <Grid xs={12}>
                          <TextField
                            disabled
                            name="status"
                            label={t("Claim state")}
                            value={translateExciseClaimStatus(viewExciseClaimDialog?.params?.status, t)}
                            fullWidth
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            disabled
                            name="liters"
                            label={t("Liters")}
                            value={values.liters}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name ? errors.name : ""}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="name"
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            disabled
                            name="claimAmountEUR"
                            label={t("Claim amount")}
                            value={values.claimAmountEUR}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name ? errors.name : ""}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="name"
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            disabled
                            name="claimAmountLocal"
                            label={t("Amount local")}
                            value={values.claimAmountLocal}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name ? errors.name : ""}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="name"
                            variant="outlined"
                            my={2}
                          />
                        </Grid>{" "}
                      </Grid>
                      <Grid item xs={12} md={9}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
                          <Box sx={{ width: "100%", overflow: "auto" }}>
                            {fileUrl === "" ? (
                              <Typography
                                sx={{
                                  fontSize: "21px",
                                  mt: 2,
                                  pt: "394px",
                                  pb: "394px",
                                  display: "flex",
                                  justifyContent: "center",
                                  border: 1,
                                  borderColor: "lightgray",
                                  width: "100%",
                                  maxWidth: 1000,
                                }}
                              >
                                <CircularProgress size={30} sx={{ mr: 2 }} />
                                {t("Loading file, please wait!")}
                              </Typography>
                            ) : fileUrl === "NoFile" || fileUrl === null ? (
                              <StyledDropzone />
                            ) : (
                              <>
                                <Dialog open={showConfirmationDialog} onClose={handleConfirmationDialogClose}>
                                  <DialogTitle>{t("Confirmation")}</DialogTitle>
                                  <DialogContent>
                                    <DialogContentText>{t("Are you sure you want to delete the invoice copy?")}</DialogContentText>
                                  </DialogContent>
                                  <DialogActions>
                                    <Button onClick={handleConfirmationDialogClose} color="primary">
                                      {t("Cancel")}
                                    </Button>
                                    <Button color="primary">{t("Delete")}</Button>
                                  </DialogActions>
                                </Dialog>
                                <object
                                  width="100%"
                                  height={800}
                                  data={fileUrl}
                                  type="application/pdf"
                                  style={{
                                    marginTop: "8px",
                                    maxWidth: 1000,
                                  }}
                                >
                                  <iframe title="file" src={fileUrl + "&embedded=true"}></iframe>
                                </object>
                              </>
                            )}
                          </Box>
                          {showReceivedPaymentDetails && (
                            <Box
                              sx={{
                                mt: 3,
                                pt: 0,
                                width: "100%",
                                maxWidth: 720,
                              }}
                            >
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                {t("Received payment details")}
                              </Typography>
                              <ClaimPaymentFields
                                claimId={viewExciseClaimDialog?.params?.claimId}
                                claimType="excise"
                                paymentReceivedDate={
                                  viewExciseClaimDialog?.params?.paymentReceivedDate ?? viewExciseClaimDialog?.params?.PaymentReceivedDate
                                }
                                paymentAmount={viewExciseClaimDialog?.params?.paymentAmount ?? viewExciseClaimDialog?.params?.PaymentAmount}
                                onSaved={handlePaymentSaved}
                              />
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>

                    {/* <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      mt={3}
                      my={2}
                      mr={4}
                    >
                      Update Excise Claim
                    </Button> */}
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      mt={3}
                      my={2}
                      onClick={() =>
                        setViewExciseClaimDialog({
                          ...viewExciseClaimDialog,
                          isOpen: false,
                        })
                      }
                    >
                      {t("Close")}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
